/**
 * This mixin contains logic for driving the Three.js scene for the spiral curve lab.
 * This was copied from the simple curve mixin and modified to support the spiral curve.
 * TODO: remove simple curve lab content and replace with spiral curve content
 */

import { mapGetters, mapActions } from "vuex";
import { StepId, CurveConstants, PointName } from "@/data/content";
import DatastoreAPI from "@/helpers/DatastoreAPI";
import { Vector3 } from "three";
import { createMeasurementLine, createLabelDiv } from "@/helpers/ThreeHelpers";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { getCalculatedMidpointCoordinates } from "@/helpers/curveLabHelpers";
const TC_POINT_SECTION_INDEX = 1;
const PI_POINT_SECTION_INDEX = 0;

const spiralCurveSceneMixin = {
  computed: {
    ...mapGetters([
      "getCurrentStepId",
      "getCurrentSectionIndex",
      "mapToDatastore",
      "getPointPositions",
      "isDebugMode"
    ])
  },
  mixins: [DatastoreAPI],
  data() {
    return {
      // Only one prism is used at a time, value is null if no prism should be visible in scene
      prismPosition: null
    };
  },
  mounted() {
    console.log("spiralCurveSceneMixin mounted");
    this.doStepOperations(this.getCurrentStepId);
  },
  methods: {
    ...mapActions([
      "updatePointPosition",
      "updateInstrumentHeight",
      "setAtPointName",
      "setHasMeasuredOnCurrentStep"
      // getAtPointName is not needed here, it's already a computed in the parent component
    ]),
    // The point that the back tangent is zeroed against, depending ont the current "At" point (where the total station is setup)
    getHzReferencePoint() {
      switch (this.getAtPointName) {
        case PointName.TS:
          return this.getPointPositions.PI;
        case PointName.SC:
          return this.getPointPositions.TS;
        case PointName.ST:
          return this.getPointPositions.PI;
        default:
          // default should never happen
          throw new Error(
            `getHzReferencePoint should never be called for this at point: ${this.getAtPointName}`
          );
      }
    },
    getRandomPointNear(position) {
      // maximum 1 meter away from the given position in either direction
      // no randomization for debug environment
      const distance = this.isDebugMode ? 0 : 1;
      const randomX = position.x + (Math.random() - 0.5) * distance;
      const randomZ = position.z + (Math.random() - 0.5) * distance;
      return new Vector3(randomX, 0, randomZ);
    },
    doStepOperations(stepId) {
      // implement operations for all steps
      // TODO: group these conditions based on the current origin point
      // Steps at the PI
      if (stepId === StepId.STAKE_OUT_TS) {
        console.log("placing TS Prism");
        this.prismPosition = this.getRandomPointNear(
          new Vector3(-CurveConstants.Ts, 0, 0)
        );
        // rotate the prism to face the camera, given the current at point
        this.rotatePrismToFaceCamera();
      } else if (stepId === StepId.STAKE_OUT_ST) {
        console.log("placing ST Prism");
        // point is T distance away from origin, PI - I counterclockwise from TC
        const stPosition = new Vector3(-CurveConstants.Ts, 0, 0);
        const rotation = Math.PI - CurveConstants.I;
        stPosition.applyAxisAngle(new Vector3(0, 1, 0), rotation);
        this.prismPosition = this.getRandomPointNear(stPosition);
        // rotate the prism to face the camera, given the current at point
        this.rotatePrismToFaceCamera();
        // End of steps at the PI
      } else if (
        stepId?.startsWith("STAKE_OUT_TP") ||
        stepId === StepId.STAKE_OUT_MP ||
        stepId === StepId.STAKE_OUT_CS ||
        stepId === StepId.STAKE_OUT_SC ||
        stepId === StepId.STAKE_OUT_CS2
      ) {
        // All the Midpoint stake placement steps have initial prism placement done the same way
        const stakeName = stepId.replace("STAKE_OUT_", "");
        // When at point SC, the Hz reference is actually offset 180 - 2A from the TS point
        const deflectionFromReferencePoint =
          this.getAtPointName === PointName.SC
            ? Math.PI - 2 * CurveConstants.A
            : 0;
        console.log("placing ", stakeName, " Prism");
        const position = getCalculatedMidpointCoordinates(
          stakeName,
          this.getPointPositions[this.getAtPointName],
          this.getHzReferencePoint(),
          deflectionFromReferencePoint
        );
        this.prismPosition = this.getRandomPointNear(position);
        // rotate the prism to face the camera, given the current at point
        this.rotatePrismToFaceCamera();
      } else if (stepId === StepId.MEASURE_ERROR_MP) {
        if (this.measurementLine) {
          // remove the measurement line if one exists
          this.removeMeasurementLine();
        }
        // Store reference to the measurement line for cleanup
        this.measurementLine = createMeasurementLine(
          this.getPointPositions.MP,
          this.getPointPositions.PI,
          this.scene
        );

        const labelDiv = createLabelDiv(
          "Distance Length",
          "black",
          "rgba(255, 255, 255, 0.9)",
          true
        );
        // hide the label initially so it only shows up when the measurement line is done animating
        labelDiv.style.visibility = "hidden";
        const label = new CSS2DObject(labelDiv);

        this.measurementLine.mesh.add(label);
      } else if (stepId === StepId.MEASURE_ERROR_CS2) {
        if (this.measurementLine) {
          // remove the measurement line if one exists
          this.removeMeasurementLine();
        }
        // Store reference to the measurement line for cleanup
        this.measurementLine = createMeasurementLine(
          this.getPointPositions.CS2,
          this.getPointPositions.CS,
          this.scene
        );

        const labelDiv = createLabelDiv(
          "Distance Length",
          "black",
          "rgba(255, 255, 255, 0.9)",
          true
        );
        // hide the label initially so it only shows up when the measurement line is done animating
        labelDiv.style.visibility = "hidden";
        const label = new CSS2DObject(labelDiv);

        this.measurementLine.mesh.add(label);
      }
    },
    updateCurrentPointPosition(position) {
      // Get the current point name from the step id
      const pointName = this.getCurrentStepId.replace("STAKE_OUT_", "");
      this.updatePointPosition({
        pointName: pointName,
        position: position
      });
    },
    removeMeasurementLine: function () {
      if (this.measurementLine) {
        this.scene.remove(this.measurementLine.mesh);
        // Remove the label from the measurement line
        this.measurementLine.mesh.children.forEach((child) => {
          if (child instanceof CSS2DObject) {
            child.element.remove(); // Remove the DOM element
            child.removeFromParent(); // Remove from Three.js scene
          }
        });
        this.measurementLine = null;
      }
    }
  },
  watch: {
    getCurrentStepId: function (newVal, oldVal) {
      // If the user backtracks from a stake placement step, before placing the stake
      if (oldVal?.startsWith("STAKE_OUT")) {
        // reset prism position
        this.prismPosition = null;
      }
      if (oldVal?.startsWith("MEASURE")) {
        // remove the measurement line when the user exits a measurement step
        this.removeMeasurementLine();
      }
      if (newVal === StepId.STAKE_OUT_CS2) {
        // hide the original CS placement
        this.setPointVisibility(PointName.CS, false);
      } else if (oldVal === StepId.STAKE_OUT_CS2) {
        // show the original CS placement
        this.setPointVisibility(PointName.CS, true);
      }
      // Reset the hasMeasuredOnCurrentStep flag when the step changes
      this.setHasMeasuredOnCurrentStep(false);
      this.doStepOperations(newVal);
    },
    getCurrentSectionIndex: function (newVal, oldVal) {
      if (
        newVal === TC_POINT_SECTION_INDEX &&
        oldVal === PI_POINT_SECTION_INDEX
      ) {
        // If the user is changing from the first section to the second section, set the "At" point to TS
        console.log("setting at point to TS");
        this.setAtPointName(PointName.TS);
      } else if (
        newVal === PI_POINT_SECTION_INDEX &&
        oldVal === TC_POINT_SECTION_INDEX
      ) {
        // If the user is changing from the second section to the first section, set the "At" point to PI
        console.log("setting at point to PI");
        this.setAtPointName(PointName.PI);
      }
    }
  }
};

export default spiralCurveSceneMixin;
